<template>
  <div class="factory-products">
    <template v-if="deviceType === 'MOBILE'">
      <H5SearchHeader
        :default-input="filter.keyword"
        @search="onSearch"
      />
      <H5FactoryHeader />
    </template>
    <SupplierHeader
      v-else
      :factory-id="$route.params.id"
      :default-input="filter.keyword"
      :show-top="false"
      @search="onSearch"
    />
    <PageContainer class="factory-products__content">
      <div
        class="factory-products__search"
        v-if="filter.keyword"
      >
        <template v-if="list.length > 0 || loading">
          Search results for
          "<span class="factory-products__keyword">{{ filter.keyword }}</span>"
        </template>
        <template v-else>
          Search no results
        </template>
      </div>
      <template v-if="list.length > 0 || loading || filter.priceStart || filter.priceEnd">
        <Filter
          class="factory-products__filter"
          :class="{
            'factory-products__sticky': width < 768
          }"
          v-model:sort="filter.sortMap"
          v-model:price-start="filter.priceStart"
          v-model:price-end="filter.priceEnd"
          @done="onReset"
        />
        <PriceTip
          class="factory-products__tip"
        />
      </template>
      <div
        class="factory-products__list"
        v-if="list.length || loading"
      >
        <component
          :is="width < 768 ? H5FactoryProductItem : FactoryProductItem"
          class="factory-products-item"
          v-for="(item, index) in list"
          :key="item.offerId"
          :item="item"
          @click="onItemClick(item)"
        />
        <FactorySkeleton v-if="loading" />
      </div>
      <SearchEmpty
        class="factory-products__empty"
        v-else-if="filter.keyword"
      />
      <Empty
        class="factory-products__empty"
        v-else
        :msg="'Sorry, no results found'"
      />
    </PageContainer>
  </div>
</template>

<script setup>
import { PageContainer } from '@/components/PageLayout'
import { Filter, PriceTip } from '@/components/Filter'
import Empty from '@/components/Empty/index.vue'

import { buriedPointUtil, isMobile, openURLWithChannelQuery } from '@/utils/utils'

import { useProductList } from './hooks/useProductList'
import SearchEmpty from './components/SearchEmpty.vue'
import FactoryProductItem from './components/FactoryProductItem.vue'
import H5FactoryProductItem from './components/H5FactoryProductItem.vue'
import FactorySkeleton from './components/FactorySkeleton.vue'
import { SupplierHeader } from '@/components/SupplierHeader'
import H5SearchHeader from './components/H5SearchHeader.vue'
import { useWindowSize } from '@/hooks/useWindowSize'
import H5FactoryHeader from './components/H5FactoryHeader.vue'
import { deviceType } from '@/utils/constants'

const route = useRoute()
const router = useRouter()
const { loading, list, filter, onReset } = useProductList()

const { width } = useWindowSize()

watch(() => filter.sortMap, () => {
  onReset()
})

const onSearch = ({ text }) => {
  if (deviceType === 'MOBILE') {
    router.push({
      name: 'factorySearch',
      params: {
        id: route.params.id,
        name: route.params.name
      },
      query: {
        keyword: text
      }
    })
  } else {
    filter.keyword = text
    onReset()
  }
}

const onItemClick = (item) => {
  if (filter.keyword) {
    buriedPointUtil({
      eventId: isMobile() ? 'HFL201' : 'PFL201',
      others: { keyword: filter.keyword }
    })
  } else {
    buriedPointUtil({ eventId: isMobile() ? 'HFL202' : 'PFL202' })
  }
  openURLWithChannelQuery(`/offer/${item.offerId}?key=${btoa(item.monthSold)}`)
}
</script>

<style lang="less" scoped>
.factory-products {
  flex: 1;
  display: flex;
  flex-direction: column;

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 8px 0;

    @media (min-width: 768px) {
      padding: 24px 0;
    }

    :deep(.page-container__content) {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }

  &__search {
    margin-top: 8px;
    padding-bottom: 4px;
    font-family: mon-700;
    font-size: 16px;
    line-height: 24px;

    @media (min-width: 768px) {
      margin-top: 0;
      margin-bottom: 16px;
      padding-bottom: 16px;
      border-bottom: 1px solid #EBF1F5;
      font-size: 20px;
      color: #232323;
    }
  }

  &__keyword {
    color: #052784;
  }

  &__filter {
    margin: 0 -12px;
    padding: 0 12px;
  }

   &__sticky {
    z-index: 1;
    position: sticky;
    top: 56px;
    margin: 0 -12px;
    padding: 8px 12px;
    background-color: #FFFFFF;
  }

  &__tip {
    margin-top: 4px;

    @media (min-width: 768px) {
      margin-top: 8px;
    }
  }

  &__list {
    padding: 16px 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 16px 13px;

    @media (min-width: 1024px) {
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 16px;
    }
  }

  &__empty {
    flex: 1;
    padding: 80px 0;
  }
}
</style>
